import { useState } from 'react';
import { useAppState } from '@/hooks/useAppState';
import ProjectCard from '@/components/feature/ProjectCard';
import EmptyState from '@/components/feature/EmptyState';
import PrivacyBadge from '@/components/feature/PrivacyBadge';

type ProjectStatus = 'active' | 'paused' | 'completed';

export default function Projects() {
  const { projects } = useAppState();
  const [statusFilter, setStatusFilter] = useState<ProjectStatus>('active');

  const tabs: { key: ProjectStatus; label: string; count: number }[] = [
    { key: 'active', label: '进行中', count: projects.filter((p) => p.status === 'active').length },
    { key: 'paused', label: '暂停', count: projects.filter((p) => p.status === 'paused').length },
    { key: 'completed', label: '已完成', count: projects.filter((p) => p.status === 'completed').length },
  ];

  const filtered = projects.filter((p) => p.status === statusFilter);

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header */}
      <header className="px-4 pt-6 pb-2"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-foreground-800 font-heading">项目</h1>
            <PrivacyBadge type="private" />
          </div>
          <button className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line text-lg"></i>
          </button>
        </div>
      </header>

      {/* Tab switcher */}
      <div className="px-4 mb-4">
        <div className="flex bg-background-100 rounded-full p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={`flex-1 text-xs py-2 rounded-full font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                statusFilter === t.key
                  ? 'bg-background-50 text-foreground-800'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`ml-1 text-[10px] ${statusFilter === t.key ? 'text-primary-600' : 'text-foreground-400'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Project list */}
      <div className="px-4 space-y-3">
        {filtered.length > 0 ? (
          filtered.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))
        ) : (
          <EmptyState
            icon="ri-folder-line"
            title="这里还没有项目"
            description="创建一个项目来组织你的待办，让每一步都朝着目标前进。"
          />
        )}
      </div>

      {/* Empty state for paused/completed */}
      {filtered.length > 0 && statusFilter === 'completed' && (
        <div className="px-4 mt-6 text-center">
          <p className="text-[10px] text-foreground-300">
            完成的每一步都值得被记录
          </p>
        </div>
      )}
    </div>
  );
}
